import { R4 } from "@ahryman40k/ts-fhir-types";

import { VaccinationResource, vaccinationResourceTypes } from "../types";
import { isDeleted, setDuplicateExtension } from "./fhir";
import { isReplaced, replaces } from "./replacements";

async function hashString(str: string): Promise<string> {
  const data = new TextEncoder().encode(str);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

async function hashObject(obj: any): Promise<string> {
  return await hashString(JSON.stringify(obj));
}

function getConceptSignature(concept: R4.ICodeableConcept): string {
  const codes = concept.coding?.map((coding) => coding.code);
  return JSON.stringify(codes);
}

function getQuantitySignature(quantity: R4.IQuantity): string {
  return JSON.stringify({
    value: quantity.value,
    unit: quantity.unit,
    comparator: quantity.comparator,
  });
}

function getAgeSignature(age: R4.IAge): string {
  return JSON.stringify({ value: age.value, unit: age.unit, comparator: age.comparator });
}

function getRangeSignature(range: R4.IRange) {
  return JSON.stringify({
    low: range.low && getQuantitySignature(range.low),
    high: range.high && getQuantitySignature(range.high),
  });
}

function getPeriodSignature(period: R4.IPeriod): string {
  return JSON.stringify({ start: period.start, end: period.end });
}

// https://fhir.ch/ig/ch-vacd/StructureDefinition-ch-vacd-ext-merging-conflict-entry-reference.html#immunization
async function getImmunizationSignature(immunization: R4.IImmunization): Promise<string> {
  const vaccineCodes = getConceptSignature(immunization.vaccineCode);
  const targetDiseaseCodes = immunization.protocolApplied
    ?.map((protocol) =>
      protocol.targetDisease
        ?.map((concept) => concept.coding?.map((coding) => coding.code).join(","))
        .join(",")
    )
    .join(",");
  const obsolete = isDeleted(immunization) || isReplaced(immunization);

  return await hashObject([
    vaccineCodes,
    immunization.occurrenceDateTime,
    targetDiseaseCodes,
    immunization.lotNumber,
    obsolete,
  ]);
}

// https://fhir.ch/ig/ch-vacd/StructureDefinition-ch-vacd-ext-merging-conflict-entry-reference.html#allergyintolerance
async function getAllergySignature(allergy: R4.IAllergyIntolerance): Promise<string> {
  const codes = allergy.code && getConceptSignature(allergy.code);
  let onset = null;
  if (allergy.onsetDateTime) {
    onset = allergy.onsetDateTime;
  } else if (allergy.onsetString) {
    onset = allergy.onsetString;
  } else if (allergy.onsetAge) {
    onset = getAgeSignature(allergy.onsetAge);
  } else if (allergy.onsetPeriod) {
    onset = getPeriodSignature(allergy.onsetPeriod);
  } else if (allergy.onsetRange) {
    onset = getRangeSignature(allergy.onsetRange);
  }
  const obsolete = isDeleted(allergy) || isReplaced(allergy);

  return await hashObject([codes, onset, obsolete]);
}

// https://fhir.ch/ig/ch-vacd/StructureDefinition-ch-vacd-ext-merging-conflict-entry-reference.html#pastillness-medicalproblems-basicimmunization-condition
async function getConditionSignature(condition: R4.ICondition): Promise<string> {
  const codes = condition.code && getConceptSignature(condition.code);
  let onset = null;
  if (condition.onsetDateTime) {
    onset = condition.onsetDateTime;
  } else if (condition.onsetString) {
    onset = condition.onsetString;
  } else if (condition.onsetAge) {
    onset = getAgeSignature(condition.onsetAge);
  } else if (condition.onsetPeriod) {
    onset = getPeriodSignature(condition.onsetPeriod);
  } else if (condition.onsetRange) {
    onset = getRangeSignature(condition.onsetRange);
  }
  const obsolete = isDeleted(condition) || isReplaced(condition);

  return await hashObject([codes, onset, obsolete]);
}

async function getObservationSignature(observation: R4.IObservation): Promise<string> {
  const codes = observation.code && getConceptSignature(observation.code);
  let effective = null;
  if (observation.effectiveDateTime) {
    effective = observation.effectiveDateTime;
  } else if (observation.effectiveInstant) {
    effective = observation.effectiveInstant;
  } else if (observation.effectivePeriod) {
    effective = getPeriodSignature(observation.effectivePeriod);
  } else if (observation.effectiveTiming) {
    effective = JSON.stringify(observation.effectiveTiming); // HACK
  }
  const obsolete = isDeleted(observation) || isReplaced(observation);

  return await hashObject([codes, effective, obsolete]);
}

export async function removeDuplicateVaccinationResources(bundle: R4.IBundle) {
  if (!bundle.entry) {
    return bundle;
  }

  const signatureMap: Record<string, VaccinationResource> = {};
  for (const entry of bundle.entry) {
    if (
      !entry?.resource?.resourceType ||
      !vaccinationResourceTypes.includes(entry.resource.resourceType)
    ) {
      continue;
    }

    const resource = entry.resource as VaccinationResource;
    const resourceType = resource.resourceType;

    let signature = null;
    if (resourceType === "Immunization") {
      signature = await getImmunizationSignature(entry.resource as R4.IImmunization);
    } else if (resourceType === "AllergyIntolerance") {
      signature = await getAllergySignature(entry.resource as R4.IAllergyIntolerance);
    } else if (resourceType === "Condition") {
      signature = await getConditionSignature(entry.resource as R4.ICondition);
    } else if (resourceType === "Observation") {
      signature = await getObservationSignature(entry.resource as R4.IObservation);
    }

    if (!signature) {
      continue;
    }

    if (signatureMap[signature]) {
      // Do not flag replacements as duplicates
      if (!isReplaced(resource) && !replaces(resource)) {
        setDuplicateExtension(signatureMap[signature], resource);
        setDuplicateExtension(resource, signatureMap[signature]);
      }
    } else {
      signatureMap[signature] = resource as VaccinationResource;
    }
  }
}
